import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Icon,
  Input,
  InputGroup,
  Mark,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';
import api from '@/utils/api';
import { Content, PlanItem } from '@/lib/types';
import { slotFor } from '@/lib/slot-color';
import { uid } from '@/pages/planner/draft';
import { PillGroup } from '@/components/ui/pill-group';
import useContentTypesStore from '@/utils/use-content-type';

interface ItemSearchProps {
  onAdd: (item: PlanItem) => void;
  onDone?: () => void;
}

// Fully-snapshotted PlanItem from a library search hit — copies the fields we
// need at add-time so later edits/renames of the source content don't retroactively
// change what's already on the plan.
function itemFromContent(content: Content): PlanItem {
  return {
    uid: uid(),
    name: content.name,
    content_id: content.id,
    content_reference_id: content.reference_id,
    content_type_id: content.content_type_id,
    content_type_name: content.content_type_name,
    custom_label: null,
  };
}

// Descriptions are authored in the rich-text editor, so a hit carries HTML.
// Parsing it (rather than sanitizing with no allowed tags) yields decoded
// text — "&" stays "&" instead of surfacing as "&amp;" in the summary line.
function summaryOf(description: string | null | undefined): string {
  if (!description) return '';
  const text = new DOMParser().parseFromString(description, 'text/html').body.textContent ?? '';
  return text.replace(/\s+/g, ' ').trim();
}

// The backend matches `name ILIKE %q% OR description ILIKE %q%` — the whole
// query as one substring — so the helpers below treat it the same way.
// Single characters match nearly every row, so they neither move the snippet
// window nor highlight; that would just paint every "d" in the list.
const MIN_MATCH_CHARS = 2;
const SNIPPET_LEAD_CHARS = 32;

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// A hit can match deep inside a long description while the one-line summary
// only ever showed its opening words. Start the summary a few words before the
// first match instead, so the part that actually matched is what's on screen.
function snippetAround(text: string, query: string): string {
  if (query.length < MIN_MATCH_CHARS) return text;
  const match = new RegExp(escapeRegExp(query), 'i').exec(text);
  if (!match || match.index <= SNIPPET_LEAD_CHARS) return text;
  const wordStart = text.indexOf(' ', match.index - SNIPPET_LEAD_CHARS);
  const start = wordStart !== -1 && wordStart < match.index ? wordStart + 1 : match.index;
  return `…${text.slice(start)}`;
}

// Wraps each case-insensitive occurrence of the query in <mark> so a coach can
// see why a hit surfaced. Splitting on a capturing group interleaves the
// matches at the odd indices.
function highlight(text: string, query: string): ReactNode {
  if (query.length < MIN_MATCH_CHARS) return text;
  return text.split(new RegExp(`(${escapeRegExp(query)})`, 'gi')).map((part, i) =>
    i % 2 === 1 ? (
      <Mark key={i} variant='subtle' colorPalette='brand' borderRadius='xs'>
        {part}
      </Mark>
    ) : (
      part
    ),
  );
}

// A brand-new item: no content yet, but its library is already chosen, so
// builder-screen #materializeCustomItems creates the draft content in that
// library on plan save (and the save-to-library heads-up lists it).
function newItem(name: string, library: { id: number; name: string }): PlanItem {
  return {
    uid: uid(),
    name,
    content_id: null,
    content_reference_id: null,
    content_type_id: library.id,
    content_type_name: library.name,
    custom_label: null,
  };
}

// Inline "+ Add content item" search — copies the debounce/cancel pattern
// from src/components/home/find-content-dialog.tsx:82-113. Renders inline
// under a section rather than in a dialog. Below the hits, "Create … as a new
// content item" swaps the result list for a small create form (name +
// library pills) so a coach files the item in a library right away instead
// of hunting for the label select on the row afterwards.
export default function ItemSearch({ onAdd, onDone }: ItemSearchProps) {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<Content[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newLibraryId, setNewLibraryId] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Populated by the navbar on workspace mount (navigation-menu.tsx) — same
  // read-only reliance as item-row.tsx.
  const contentTypes = useContentTypesStore((s) => s.contentTypes);
  const libraryPills = useMemo(
    () => contentTypes.map((t) => ({ value: t.id, label: t.name })),
    [contentTypes],
  );

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  // Debounce the query (200ms).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  // Run search whenever the debounced query changes.
  useEffect(() => {
    if (!workspaceId) return;
    const q = debouncedQuery;
    if (!q) {
      setResults([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    let cancelled = false;
    api
      .get(
        // include_personal widens the published-only scope with the caller's own
        // unpublished items, so personal-library content is plannable too.
        `contents/search/${workspaceId}?q=${encodeURIComponent(q)}&status=published&include_personal=1&limit=8`,
      )
      .then((res) => {
        if (cancelled) return;
        setResults(res.data.data || []);
      })
      .catch(() => {
        if (!cancelled) setResults([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, workspaceId]);

  const handleAddContent = (content: Content) => {
    onAdd(itemFromContent(content));
    setQuery('');
    setResults([]);
    onDone?.();
  };

  const startCreate = () => {
    if (!debouncedQuery) return;
    setNewName(debouncedQuery);
    setNewLibraryId(null);
    setIsCreating(true);
  };

  const cancelCreate = () => {
    setIsCreating(false);
    inputRef.current?.focus();
  };

  const newLibrary = contentTypes.find((t) => t.id === newLibraryId);
  const canCreate = newName.trim().length > 0 && newLibrary != null;

  const handleCreate = () => {
    if (!newLibrary || !canCreate) return;
    onAdd(newItem(newName.trim(), { id: newLibrary.id, name: newLibrary.name }));
    setQuery('');
    setResults([]);
    setIsCreating(false);
    onDone?.();
  };

  return (
    <Box bg='bg.subtle' borderRadius='md' p={3}>
      <InputGroup startElement={<Icon as={MagnifyingGlassIcon} w={4} h={4} color='fg.subtle' />}>
        <Input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            // Typing again means the coach went back to searching.
            setIsCreating(false);
          }}
          placeholder='Search drills, skills, tactics...'
          bg='bg'
          borderColor='border'
          size='sm'
        />
      </InputGroup>

      {isCreating && (
        <Stack gap={3} mt={3}>
          <Text textStyle='eyebrow'>New content item</Text>
          <Box>
            <Text textStyle='eyebrow' mb={1}>
              Name
            </Text>
            <Input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder='Content item name'
              bg='bg'
              borderColor='border'
              size='sm'
            />
          </Box>
          <Box>
            <Text textStyle='eyebrow' mb={2}>
              Which library does it go in?
            </Text>
            {libraryPills.length > 0 ? (
              <PillGroup
                size='sm'
                gap={2}
                items={libraryPills}
                value={newLibraryId}
                onChange={setNewLibraryId}
              />
            ) : (
              <Text fontSize='sm' color='fg.muted'>
                This workspace has no content libraries yet.
              </Text>
            )}
            <Text fontSize='xs' color='fg.muted' mt={2}>
              Pick a library so the item is filed where your coaches expect to find it.
            </Text>
          </Box>
          <Flex justify='space-between' align='center' gap={2}>
            <Button variant='outline' size='sm' onClick={cancelCreate}>
              Cancel
            </Button>
            <Button colorPalette='brand' size='sm' onClick={handleCreate} disabled={!canCreate}>
              Create &amp; add to section
            </Button>
          </Flex>
        </Stack>
      )}

      {!isCreating && isLoading && (
        <Flex justify='center' py={3}>
          <Spinner size='sm' color='fg.subtle' />
        </Flex>
      )}

      {!isCreating && !isLoading && debouncedQuery && (
        <Stack gap={1} mt={2}>
          {results.length === 0 && (
            <Text fontSize='sm' color='fg.muted' px={2} py={2}>
              Nothing in your library or partner libraries matches &ldquo;{debouncedQuery}&rdquo;.
            </Text>
          )}
          {results.slice(0, 8).map((content) => {
            const slot = slotFor(String(content.content_type_id));
            const summary = snippetAround(summaryOf(content.description), debouncedQuery);
            return (
              <Flex
                key={content.id}
                as='button'
                type='button'
                onClick={() => handleAddContent(content)}
                direction='column'
                align='stretch'
                gap={0.5}
                px={2}
                py={2}
                borderRadius='md'
                width='100%'
                textAlign='left'
                transition='background 0.15s'
                _hover={{ bg: 'bg' }}
              >
                <Flex align='center' gap={2}>
                  <Text fontSize='sm' fontWeight='medium' color='fg.strong' flex='1' lineClamp={1}>
                    {content.name ? highlight(content.name, debouncedQuery) : 'Untitled'}
                  </Text>
                  {content.status !== 'published' && (
                    <Text
                      as='span'
                      fontSize='xs'
                      fontWeight='semibold'
                      px={2}
                      py={0.5}
                      borderRadius='full'
                      bg='bg.muted'
                      color='fg.muted'
                      flexShrink={0}
                    >
                      Personal
                    </Text>
                  )}
                  {content.content_type_name && (
                    <Text
                      as='span'
                      fontSize='xs'
                      fontWeight='semibold'
                      px={2}
                      py={0.5}
                      borderRadius='full'
                      bg={`slot${slot}.bg`}
                      color={`slot${slot}.fg`}
                      flexShrink={0}
                    >
                      {content.content_type_name}
                    </Text>
                  )}
                </Flex>
                {summary && (
                  <Text fontSize='xs' color='fg.muted' lineClamp={1}>
                    {highlight(summary, debouncedQuery)}
                  </Text>
                )}
              </Flex>
            );
          })}

          <Flex
            as='button'
            type='button'
            onClick={startCreate}
            align='center'
            justify='center'
            gap={2}
            mt={1}
            px={3}
            py={3}
            borderRadius='md'
            borderWidth='1px'
            borderColor='brand.solid'
            bg='bg.panel'
            width='100%'
            transition='background 0.15s'
            _hover={{ bg: 'brand.subtle' }}
          >
            <Icon as={PlusIcon} w={4} h={4} color='brand.solid' flexShrink={0} />
            <Text fontSize='sm' fontWeight='600' color='brand.solid'>
              Create &ldquo;{debouncedQuery}&rdquo; as a new content item
            </Text>
          </Flex>
        </Stack>
      )}
    </Box>
  );
}
